import { toSafeLowerString } from './toSafeLowerString.js';
import { toPlainText } from './toPlainText.js';

const asObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
};

const resolveInteractionResponseSummary = ({ interactionType, responseSource, responseObject, payload }) => {
  const isConnectionSetupInteraction = interactionType === 'connection_setup';
  const isPermissionApprovalInteraction = interactionType === 'permission_approval';
  const messageCandidates = [responseObject?.message, responseObject?.guidance, responseObject?.statusMessage, payload?.message, payload?.text, payload?.content];

  for (const candidate of messageCandidates) {
    const summary = toPlainText(candidate).trim();

    if (summary) {
      return summary;
    }
  }

  if (isPermissionApprovalInteraction && responseObject?.decision === true) {
    if (responseObject?.remember === true) {
      return 'Approved. I will remember this choice.';
    }

    return 'Approved.';
  }

  if (isConnectionSetupInteraction) {
    if (responseObject?.connected === true) {
      return 'Connection ready.';
    }

    if (responseObject?.dismissed === true || toSafeLowerString(responseObject?.source) === 'modal_close') {
      return 'Authentication dismissed.';
    }

    if (responseObject?.connected === false) {
      return 'Connection not ready yet.';
    }
  }

  if (typeof responseSource === 'string' || typeof responseSource === 'number' || typeof responseSource === 'boolean') {
    return toPlainText(responseSource).trim();
  }

  if (responseObject && Object.keys(responseObject).length > 0) {
    return 'Interaction response submitted.';
  }

  return '';
};

const looksLikePermissionApprovalResponse = ({ responseObject, payload }) => {
  const response = asObject(responseObject) || {};
  const payloadObject = asObject(payload) || {};
  const payloadResponse = asObject(payloadObject?.response) || {};

  const decisionCandidates = [response?.decision, payloadObject?.decision, payloadResponse?.decision];
  const rememberCandidates = [response?.remember, payloadObject?.remember, payloadResponse?.remember];

  if (decisionCandidates.some((candidate) => typeof candidate === 'boolean')) {
    return true;
  }

  if (rememberCandidates.some((candidate) => typeof candidate === 'boolean')) {
    return true;
  }

  return false;
};

const normalizePieveThreadEventForPresentation = (event) => {
  const eventObject = asObject(event);

  if (!eventObject) {
    return event;
  }

  const eventType = toSafeLowerString(eventObject?.type);
  const payload = asObject(eventObject?.payload) || {};
  const interaction = asObject(payload?.interaction) || payload;
  const approval = asObject(interaction?.approval) || asObject(payload?.approval);
  const runId = eventObject?.runId || payload?.runId || interaction?.runId || approval?.runId || null;
  const interactionType = toSafeLowerString(eventObject?.interactionType || interaction?.interactionType || interaction?.type || interaction?.kind || payload?.interactionType);
  const isApprovalInteraction = interactionType === 'permission_approval' || interactionType === 'thread_mode_conversion' || Boolean(approval);

  if (eventType === 'approval' || (eventType === 'interaction' && isApprovalInteraction)) {
    const rememberEnabledFromPayload = eventObject?.rememberEnabled ?? approval?.rememberEnabled ?? interaction?.rememberEnabled;
    const alwaysAllowHidden = Boolean(eventObject?.alwaysAllowHidden ?? approval?.alwaysAllowHidden ?? interaction?.alwaysAllowHidden);
    const rememberEnabled = typeof rememberEnabledFromPayload === 'boolean' ? rememberEnabledFromPayload : !alwaysAllowHidden;

    const normalizedApprovalEvent = {
      ...eventObject,
      type: 'approval',
      approvalId: eventObject?.approvalId || approval?.approvalId || interaction?.approvalId || interaction?.interactionId || eventObject?.id,
      interactionId: eventObject?.interactionId || interaction?.interactionId || approval?.approvalId || interaction?.approvalId || eventObject?.id,
      interactionType: interactionType || 'permission_approval',
      targetMode: eventObject?.targetMode || approval?.targetMode || interaction?.targetMode || null,
      title: eventObject?.title || approval?.title || interaction?.title || interaction?.prompt || 'Approve action',
      detail: toPlainText(approval?.detail || interaction?.detail || eventObject?.detail || ''),
      rememberEnabled,
      denyLabel: eventObject?.denyLabel || approval?.denyLabel || interaction?.denyLabel || approval?.alwaysDenyLabel || interaction?.alwaysDenyLabel || 'No',
      approveOnceLabel: eventObject?.approveOnceLabel || approval?.approveOnceLabel || interaction?.approveOnceLabel || 'Yes',
      approveRememberLabel: eventObject?.approveRememberLabel || approval?.approveRememberLabel || interaction?.approveRememberLabel || approval?.alwaysAllowLabel || interaction?.alwaysAllowLabel || "Yes and don't ask again"
    };

    if (!runId) {
      return normalizedApprovalEvent;
    }

    return {
      ...normalizedApprovalEvent,
      runId
    };
  }

  if (eventType === 'interaction_response') {
    const interactionResponse = asObject(eventObject?.response) || asObject(payload?.response) || asObject(payload);
    const normalizedInteractionType = interactionType || (looksLikePermissionApprovalResponse({ responseObject: interactionResponse, payload }) ? 'permission_approval' : 'generic_prompt');
    const isPermissionApproval = normalizedInteractionType === 'permission_approval';
    const isDeniedPermissionResponse = isPermissionApproval && interactionResponse?.decision === false;
    const normalizedInteractionId = eventObject?.interactionId || interaction?.interactionId || payload?.interactionId || null;
    const isConnectionSetupInteraction = normalizedInteractionType === 'connection_setup';

    if (isDeniedPermissionResponse) {
      const guidanceText = toPlainText(interactionResponse?.guidance || interactionResponse?.message || payload?.message || payload?.text).trim();
      const denialText = guidanceText || 'No';
      const deniedApprovalEvent = {
        ...eventObject,
        id: normalizedInteractionId ? `${normalizedInteractionId}:response` : eventObject?.id,
        type: 'bubble',
        className: 'user',
        interactionId: normalizedInteractionId,
        interactionType: normalizedInteractionType,
        response: interactionResponse,
        lines: [{ text: denialText }]
      };

      if (!runId) {
        return deniedApprovalEvent;
      }

      return {
        ...deniedApprovalEvent,
        runId
      };
    }

    if (isConnectionSetupInteraction) {
      const normalizedConnectionInteractionEvent = {
        ...eventObject,
        id: normalizedInteractionId ? `${normalizedInteractionId}:response` : eventObject?.id,
        type: 'interaction',
        interactionId: normalizedInteractionId,
        interactionType: normalizedInteractionType,
        response: interactionResponse,
        title: eventObject?.title || interaction?.title || payload?.title || 'Connect to continue',
        detail: toPlainText(eventObject?.detail || interaction?.detail || payload?.detail || ''),
        payload
      };

      if (!runId) {
        return normalizedConnectionInteractionEvent;
      }

      return {
        ...normalizedConnectionInteractionEvent,
        runId
      };
    }

    const summary =
      resolveInteractionResponseSummary({
        interactionType: normalizedInteractionType,
        responseSource: eventObject?.response || payload?.response || payload,
        responseObject: interactionResponse,
        payload
      }) || 'Interaction response submitted.';
    const normalizedInteractionResponseEvent = {
      ...eventObject,
      type: 'line',
      className: 'cmd',
      interactionId: normalizedInteractionId,
      interactionType: normalizedInteractionType,
      response: interactionResponse,
      segments: [{ text: summary, tone: 'muted' }]
    };

    if (!runId) {
      return normalizedInteractionResponseEvent;
    }

    return {
      ...normalizedInteractionResponseEvent,
      runId
    };
  }

  return eventObject;
};

const normalizePieveThreadEventsForPresentation = (events = []) => {
  if (!Array.isArray(events)) {
    return [];
  }

  return events.map((event) => {
    return normalizePieveThreadEventForPresentation(event);
  });
};

export { normalizePieveThreadEventForPresentation, normalizePieveThreadEventsForPresentation };
export default normalizePieveThreadEventForPresentation;
